import types from '../../mutation-types';
import getters, { getSelectedChatConversation } from './getters';
import actions from './actions';
import { findPendingMessageIndex } from './helpers';
import { getMessageSummaryUpdates } from './helpers/messageSummary';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { CONTENT_TYPES } from 'dashboard/components-next/message/constants.js';
import {
  prependUniqueMessages,
  mergeHistoryWindow,
} from './helpers/historyRequests';

const state = {
  allConversations: [],
  composerRevalidating: false,
  attachments: {},
  listLoadingStatus: true,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  selectedChatId: null,
  appliedFilters: [],
  appliedFiltersSortBy: null,
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  conversationFilters: {},
  copilotAssistant: {},
  autoTranslating: {},
};

const getConversationById = _state => conversationId => {
  return _state.allConversations.find(c => c.id === conversationId);
};

const preserveConversationMessageState = (
  conversation,
  existingConversation
) => ({
  ...conversation,
  allMessagesLoaded: existingConversation.allMessagesLoaded,
  messages: existingConversation.messages,
  dataFetched: existingConversation.dataFetched,
  historyHasMoreAfter: existingConversation.historyHasMoreAfter,
  historyError: existingConversation.historyError,
  historyErrorAfter: existingConversation.historyErrorAfter,
  historyCursor: existingConversation.historyCursor,
  historyLatestObservedId: existingConversation.historyLatestObservedId,
});

// mutations
export const mutations = {
  [types.UPDATE_CONVERSATION_LIST_ACCESS](
    _state,
    { conversationId, listAccess }
  ) {
    const conversation = getConversationById(_state)(conversationId);
    if (conversation) conversation.permission_set_list_access = listAccess;
  },
  [types.CLEAR_CONVERSATION_ACCESS](_state, { preserveComposer = false } = {}) {
    const active =
      preserveComposer && getConversationById(_state)(_state.selectedChatId);
    _state.composerRevalidating = Boolean(active);
    _state.listLoadingStatus = false;
    _state.autoTranslating = {};
    _state.allConversations = active ? [active] : [];
    _state.selectedChatId = active ? active.id : null;
    _state.attachments = {};
    _state.contextMenuChatId = null;
    _state.conversationParticipants = [];
    _state.conversationLastSeen = null;
    _state.copilotAssistant = {};
  },
  [types.CONFIRM_RECONNECTED_CONVERSATION](_state, conversation) {
    const index = _state.allConversations.findIndex(
      item => item.id === conversation.id
    );
    if (index !== -1) {
      const cursor = Math.max(
        0,
        ...conversation.messages.map(message => message.id)
      );
      _state.allConversations[index] = {
        ...conversation,
        historyCursor: cursor,
        historyLatestObservedId: cursor,
        historyHasMoreAfter: true,
      };
    }
  },
  [types.FINISH_COMPOSER_REVALIDATION](_state) {
    _state.composerRevalidating = false;
  },
  [types.REVOKE_CONVERSATION_ACCESS](_state, conversationId) {
    _state.allConversations = _state.allConversations.filter(
      c => c.id !== conversationId
    );
    const { [conversationId]: _revoked, ...attachments } = _state.attachments;
    _state.attachments = attachments;
    if (_state.contextMenuChatId === conversationId)
      _state.contextMenuChatId = null;
    if (_state.selectedChatId === conversationId) {
      _state.composerRevalidating = false;
      _state.selectedChatId = null;
      _state.conversationParticipants = [];
      _state.conversationLastSeen = null;
      _state.copilotAssistant = {};
    }
  },
  [types.SET_CONVERSATION_PARTICIPANTS](_state, { conversationId, data }) {
    const conversation = getConversationById(_state)(conversationId);
    if (conversation) {
      conversation.meta.participant_ids = data.map(
        participant => participant.id
      );
    }
  },
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => c.id === conversation.id
      );
      if (indexInCurrentList < 0) {
        newAllConversations.push(conversation);
      } else if (
        conversation.updated_at <
        newAllConversations[indexInCurrentList].updated_at
      ) {
        // A list snapshot older than the record already applied (for example
        // by an access-gain refresh) must not undo it, as UPDATE_CONVERSATION.
      } else if (conversation.id !== _state.selectedChatId) {
        // If the conversation is already in the list, replace it
        // Added this to fix the issue of the conversation not being updated
        // When reconnecting to the websocket. If the selectedChatId is not the same as
        // the conversation.id in the store, replace the existing conversation with the new one
        newAllConversations[indexInCurrentList] = conversation;
      } else {
        // If the conversation is already in the list and selectedChatId is the same,
        // replace all data except the messages array, attachments, dataFetched, allMessagesLoaded
        const existingConversation = newAllConversations[indexInCurrentList];
        newAllConversations[indexInCurrentList] =
          preserveConversationMessageState(conversation, existingConversation);
      }
    });
    _state.allConversations = newAllConversations;
  },
  [types.REPLACE_CONVERSATION_LIST](_state, conversationList) {
    const selectedConversation = getConversationById(_state)(
      _state.selectedChatId
    );
    const replacementList = [...conversationList];
    if (selectedConversation) {
      const selectedConversationIndex = replacementList.findIndex(
        conversation => conversation.id === selectedConversation.id
      );
      if (selectedConversationIndex === -1) {
        replacementList.push(selectedConversation);
      } else {
        replacementList[selectedConversationIndex] =
          preserveConversationMessageState(
            replacementList[selectedConversationIndex],
            selectedConversation
          );
      }
    }
    _state.allConversations = replacementList;
  },
  [types.EMPTY_ALL_CONVERSATION](_state) {
    _state.allConversations = [];
    _state.selectedChatId = null;
  },
  [types.SET_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = true;
    }
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = false;
    }
  },
  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (data.length) {
      const [chat] = _state.allConversations.filter(c => c.id === id);
      prependUniqueMessages(chat.messages, data);
    }
  },
  [types.SET_CONVERSATION_HISTORY_WINDOW](
    _state,
    { id, data, hasMoreAfter, allMessagesLoaded, latestId }
  ) {
    const chat = getConversationById(_state)(id);
    chat.messages = mergeHistoryWindow(chat.messages, data, hasMoreAfter);
    chat.historyCursor = Math.max(0, ...data.map(message => message.id));
    chat.historyLatestObservedId = Math.max(
      chat.historyLatestObservedId || 0,
      latestId || 0,
      chat.historyCursor
    );
    chat.historyHasMoreAfter =
      hasMoreAfter || chat.historyLatestObservedId > chat.historyCursor;
    chat.allMessagesLoaded = allMessagesLoaded;
    chat.historyError = false;
    chat.historyErrorAfter = undefined;
  },
  [types.APPEND_CONVERSATION_HISTORY](_state, { id, data, hasMoreAfter }) {
    const chat = getConversationById(_state)(id);
    chat.messages = mergeHistoryWindow(
      chat.messages,
      [...chat.messages, ...data],
      hasMoreAfter
    );
    if (data.length)
      chat.historyCursor = Math.max(...data.map(message => message.id));
    chat.historyHasMoreAfter =
      hasMoreAfter || chat.historyLatestObservedId > chat.historyCursor;
    if (chat.historyError === 'newer') chat.historyError = false;
  },
  [types.SET_CONVERSATION_HISTORY_ERROR](_state, { id, error, after }) {
    const chat = getConversationById(_state)(id);
    chat.historyError = error;
    chat.historyErrorAfter = ['initial', 'target'].includes(error)
      ? after
      : undefined;
    if (['initial', 'target'].includes(error) && chat.dataFetched !== true)
      chat.dataFetched = false;
  },
  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    _state.attachments[id] = [...data];
  },

  [types.CLEAR_CHAT_DATA_FETCHED](_state, conversationId) {
    getConversationById(_state)(conversationId).dataFetched = false;
  },
  [types.SET_CHAT_DATA_FETCHED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.dataFetched = true;
    }
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
    }
  },

  [types.ASSIGN_AGENT](_state, { conversationId, assignee, assigneeType }) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.meta.assignee = assignee;
      const inferredAssigneeType = assignee ? 'User' : null;
      chat.meta.assignee_type =
        assigneeType === undefined ? inferredAssigneeType : assigneeType;
    }
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.meta.team = team;
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat && lastActivityAt > (chat.last_activity_at || 0)) {
      chat.last_activity_at = lastActivityAt;
    }
  },
  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.priority = priority;
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](
    _state,
    { conversationId, customAttributes }
  ) {
    const conversation = _state.allConversations.find(
      c => c.id === conversationId
    );
    if (conversation) {
      conversation.custom_attributes = {
        ...conversation.custom_attributes,
        ...customAttributes,
      };
    }
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};
    conversation.snoozed_until = snoozedUntil;
    conversation.status = status;
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = true;
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = false;
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    // early return if the message has not been sent, or has no attachments
    if (
      message.status !== MESSAGE_STATUS.SENT ||
      !message.attachments?.length
    ) {
      return;
    }

    const id = message.conversation_id;
    const existingAttachments = _state.attachments[id] || [];

    const attachmentsToAdd = message.attachments.filter(attachment => {
      // if the attachment is not already in the store, add it
      // this is to prevent duplicates
      return !existingAttachments.some(
        existingAttachment => existingAttachment.id === attachment.id
      );
    });

    // replace the attachments in the store
    _state.attachments[id] = [...existingAttachments, ...attachmentsToAdd];
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    if (message.status !== MESSAGE_STATUS.SENT) return;

    const { conversation_id: id } = message;
    const existingAttachments = _state.attachments[id] || [];
    if (!existingAttachments.length) return;

    _state.attachments[id] = existingAttachments.filter(attachment => {
      return attachment.message_id !== message.id;
    });
  },

  [types.ADD_MESSAGE]({ allConversations, selectedChatId }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });
    if (!chat) return;

    const latestKnownMessageId = chat.messages.reduce(
      (latest, item) =>
        Math.max(latest, typeof item.id === 'number' ? item.id : 0),
      chat.historyLatestObservedId || 0
    );
    if (typeof message.id === 'number') {
      chat.historyLatestObservedId = Math.max(latestKnownMessageId, message.id);
    }

    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    const previousMessage = chat.messages[pendingMessageIndex];
    const isNewOrAcknowledgedMessage =
      !previousMessage || typeof previousMessage.id !== 'number';
    if (pendingMessageIndex !== -1) {
      chat.messages[pendingMessageIndex] = message;
      if (message.echo_id && message.echo_id !== message.id) {
        chat.messages = chat.messages.filter(
          item => item.id !== message.echo_id
        );
      }
    } else {
      // Do not bridge a missing forward page with an isolated realtime row.
      if (!chat.historyHasMoreAfter || typeof message.id !== 'number') {
        chat.messages.push(message);
      }
      if (selectedChatId === conversationId) {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
          passive: true,
          conversationId,
        });
      }
    }
    // Delayed events still add their message, but must not roll back the summary.
    if (isNewOrAcknowledgedMessage) {
      Object.assign(
        chat,
        getMessageSummaryUpdates(chat, message, latestKnownMessageId)
      );
    }
  },

  [types.SET_AUTO_TRANSLATING](_state, { locale, messageIds, translating }) {
    _state.autoTranslating[locale] ??= {};
    messageIds.forEach(id => {
      _state.autoTranslating[locale][id] = translating;
    });
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    const exists = _state.allConversations.some(c => c.id === conversation.id);
    if (!exists) {
      _state.allConversations.push(conversation);
    }
  },

  [types.DELETE_CONVERSATION](_state, conversationId) {
    _state.allConversations = _state.allConversations.filter(
      c => c.id !== conversationId
    );
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    const index = allConversations.findIndex(c => c.id === conversation.id);

    if (index > -1) {
      const selectedConversation = allConversations[index];

      // ignore out of order events
      if (conversation.updated_at < selectedConversation.updated_at) {
        return;
      }

      const { messages, ...updates } = conversation;
      allConversations[index] = { ...selectedConversation, ...updates };
      if (_state.selectedChatId === conversation.id) {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
          passive: true,
          conversationId: conversation.id,
        });
      }
    } else {
      const { conversationType } = _state.conversationFilters || {};
      const { MENTION, PARTICIPATING } = wootConstants.CONVERSATION_TYPE;
      if (![MENTION, PARTICIPATING].includes(conversationType)) {
        _state.allConversations.push(conversation);
      }
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (chat) {
      chat.agent_last_seen_at = lastSeen;
      chat.unread_count = unreadCount;
    }
  },
  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  // Update assignee on action cable message
  [types.UPDATE_ASSIGNEE](_state, payload) {
    const chat = getConversationById(_state)(payload.id);
    if (chat) {
      chat.meta.assignee = payload.assignee;
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.meta.sender = payload;
    }
  },

  [types.UPDATE_MESSAGE_CALL_STATUS](
    _state,
    { conversationId, callStatus, callSid }
  ) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    const message = (chat.messages || []).find(
      m =>
        m.content_type === CONTENT_TYPES.VOICE_CALL &&
        m.call?.provider_call_id === callSid
    );
    if (!message?.call) return;

    message.call = { ...message.call, status: callStatus };
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.can_reply = canReply;
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    _state.allConversations = chats;
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.SET_CONVERSATION_FILTERS_SORT](_state, sortBy) {
    _state.appliedFiltersSortBy = sortBy;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
    _state.appliedFiltersSortBy = null;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },
  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },
  [types.SET_INBOX_CAPTAIN_ASSISTANT](_state, data) {
    _state.copilotAssistant = data.assistant;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
